//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  isRouteErrorResponse, useLocation, useRouteError
} from 'react-router';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button,
  Container, Divider, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import copy from 'copy-to-clipboard';
import Layout from '@/components/Layout/Layout';
import { useSnackbar } from '@/hooks/useSnackbar';
import NotFoundPage from '@/pages/PageNotFound';

export default function RootErrorPage() {
  const error = useRouteError();
  const location = useLocation();
  const showSnackbar = useSnackbar();

  // 1. Delegate 404s to your existing NotFoundPage
  if (isRouteErrorResponse(error) && error.status === 404)
    return <NotFoundPage />;

  // 2. Safely extract error details
  const errorMessage = error instanceof Error ?
                       error.message : (isRouteErrorResponse(error) ?
                       error.statusText : 'Unknown error');
  const errorStack = error instanceof Error ? error.stack :
                                              'No stack trace available';

  const handleCopy = () => {
    const textToCopy =
        `ROUTE:\n${location.pathname}\n\nERROR:\n${errorMessage}\n\nSTACK:\n${errorStack}`;
    copy(textToCopy);
    showSnackbar('Error message copied to clipboard!', 'success');
  };

  const handleRetry = () => {
    // For a generic route error, a full reload is often the safest "Retry"
    window.location.reload();
  };

  return (
    <Layout selectedMenuItem="None" menuClickCallback={() => {}}>
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '60vh' // Added height so it centers vertically
        }}
      >
        <Typography variant="h5" sx={{ marginY: 1 }}>
          😨 Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          {`An unexpected error occurred. Please contact customer support and provide the details below.
          Try refreshing the page or clicking "Retry" below.`}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', width: '100%' }}>
          <Accordion sx={{ textAlign: 'left', flex: '1' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Show Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h5" sx={{marginBottom: 1}}>
                Route:
              </Typography>
              <Typography variant="body1">
                {location.pathname}
              </Typography>

              <Divider sx={{marginY: 1}} />

              <Typography variant="h5" sx={{marginBottom: 1}}>
                Error:
              </Typography>
              <Typography variant="body1">
                {errorMessage}
              </Typography>

              <Divider sx={{marginY: 1}} />

              <Typography variant="h5" sx={{marginBottom: 1}}>
                Stack:
              </Typography>
              {/* Using 'component="pre"' preserves whitespace for stack traces */}
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                {errorStack}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
            <Button variant="outlined" color="primary" onClick={handleCopy} sx={{ minWidth: 120, height: 40, marginTop: 1, width: 'auto', flexGrow: 0 }}>
              Copy Error
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Button variant="contained" color="primary" onClick={handleRetry} sx={{marginTop: 2}}>
            Retry
          </Button>
        </Box>
      </Container>
    </Layout>
  );
}
